import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useAuth } from '../../contexts/AuthContext';
import Coachmark from './Coachmark';
import {
  hasCompletedTour,
  markDailyTipShown,
  markTourComplete,
  setUserKey,
  shouldShowDailyTip,
} from './coachmarkStorage';

const CoachmarkContext = createContext(null);

function escapeAttr(value) {
  if (typeof CSS !== 'undefined' && typeof CSS.escape === 'function') {
    return CSS.escape(value);
  }
  return String(value).replace(/\\/g, '\\\\').replace(/"/g, '\\"');
}

/**
 * Resolve a step.anchor to a live element.
 * `anchor` may be a registry id, a `data-sg-coach` value (e.g. "swap"),
 * a CSS selector, or an Element.
 */
export function resolveCoachAnchor(anchor, registry) {
  if (!anchor) return null;
  if (typeof Element !== 'undefined' && anchor instanceof Element) {
    return document.contains(anchor) ? anchor : null;
  }
  if (typeof anchor !== 'string') return null;

  const registered = registry?.get(anchor);
  if (registered && document.contains(registered)) return registered;

  try {
    const byData = document.querySelector(`[data-sg-coach="${escapeAttr(anchor)}"]`);
    if (byData) return byData;
  } catch {
    /* invalid attr */
  }

  try {
    return document.querySelector(anchor);
  } catch {
    return null;
  }
}

export function CoachmarkProvider({ children }) {
  const auth = useAuth();
  const user = auth?.user;
  const registryRef = useRef(new Map());
  const [registryVersion, setRegistryVersion] = useState(0);
  const [queue, setQueue] = useState([]);
  const [index, setIndex] = useState(0);
  const [tourId, setTourId] = useState(null);
  const [targetRect, setTargetRect] = useState(null);
  const missFramesRef = useRef(0);

  useEffect(() => {
    const key = user?.username || user?.id || 'anon';
    setUserKey(key);
  }, [user]);

  const registerAnchor = useCallback((id, element) => {
    if (!id) return;
    if (element) {
      registryRef.current.set(id, element);
    } else {
      registryRef.current.delete(id);
    }
    setRegistryVersion((version) => version + 1);
  }, []);

  const finishTour = useCallback(
    (completed) => {
      if (completed && tourId) {
        markTourComplete(tourId);
      }
      setQueue([]);
      setIndex(0);
      setTourId(null);
      setTargetRect(null);
      missFramesRef.current = 0;
    },
    [tourId]
  );

  const skip = useCallback(() => {
    finishTour(true);
  }, [finishTour]);

  const next = useCallback(() => {
    if (index >= queue.length - 1) {
      finishTour(true);
      return;
    }
    missFramesRef.current = 0;
    setTargetRect(null);
    setIndex((current) => current + 1);
  }, [finishTour, index, queue.length]);

  const startTour = useCallback((steps, options = {}) => {
    const nextTourId = options.tourId || null;
    if (nextTourId && hasCompletedTour(nextTourId) && !options.force) {
      return false;
    }
    const list = Array.isArray(steps) ? steps.filter((step) => step && step.id) : [];
    if (!list.length) return false;
    setTourId(nextTourId);
    setQueue(list);
    setIndex(0);
    setTargetRect(null);
    missFramesRef.current = 0;
    return true;
  }, []);

  const currentStep = queue[index] || null;

  useEffect(() => {
    if (!currentStep) {
      setTargetRect(null);
      return undefined;
    }

    let cancelled = false;
    let frame;
    let found = false;

    const measure = (el) => {
      const rect = el.getBoundingClientRect();
      if (rect.width > 0 || rect.height > 0) {
        setTargetRect(rect);
        return true;
      }
      return false;
    };

    const onScrollOrResize = () => {
      const el = resolveCoachAnchor(currentStep.anchor, registryRef.current);
      if (el) measure(el);
    };

    const tick = () => {
      if (cancelled) return;
      const el = resolveCoachAnchor(currentStep.anchor, registryRef.current);
      if (el && measure(el)) {
        missFramesRef.current = 0;
        if (!found) {
          found = true;
          window.addEventListener('scroll', onScrollOrResize, true);
          window.addEventListener('resize', onScrollOrResize);
        }
        return;
      }
      missFramesRef.current += 1;
      if (missFramesRef.current > 24) {
        // Anchor never appeared — skip this step without trapping the UI.
        if (index >= queue.length - 1) {
          finishTour(false);
        } else {
          missFramesRef.current = 0;
          setTargetRect(null);
          setIndex((current) => current + 1);
        }
        return;
      }
      frame = requestAnimationFrame(tick);
    };

    frame = requestAnimationFrame(tick);
    return () => {
      cancelled = true;
      if (frame) cancelAnimationFrame(frame);
      window.removeEventListener('scroll', onScrollOrResize, true);
      window.removeEventListener('resize', onScrollOrResize);
    };
  }, [currentStep, finishTour, index, queue.length, registryVersion]);

  useEffect(() => {
    if (!currentStep) return undefined;
    const onKey = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        skip();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [currentStep, skip]);

  const value = useMemo(
    () => ({
      startTour,
      skip,
      next,
      currentStep,
      stepIndex: index,
      stepCount: queue.length,
      isActive: Boolean(currentStep),
      registerAnchor,
    }),
    [currentStep, index, next, queue.length, registerAnchor, skip, startTour]
  );

  return (
    <CoachmarkContext.Provider value={value}>
      {children}
      {currentStep && targetRect ? (
        <Coachmark
          step={currentStep}
          targetRect={targetRect}
          stepIndex={index}
          stepCount={queue.length}
          onNext={next}
          onSkip={skip}
        />
      ) : null}
    </CoachmarkContext.Provider>
  );
}

export function useCoachmarks() {
  const ctx = useContext(CoachmarkContext);
  if (!ctx) {
    throw new Error('useCoachmarks must be used within CoachmarkProvider');
  }
  return ctx;
}

/** Callback ref: <button ref={useCoachAnchor('swap')} data-sg-coach="swap"> */
export function useCoachAnchor(id) {
  const { registerAnchor } = useCoachmarks();
  return useCallback(
    (node) => {
      registerAnchor(id, node);
    },
    [id, registerAnchor]
  );
}

export {
  hasCompletedTour,
  markDailyTipShown,
  markTourComplete,
  setUserKey,
  shouldShowDailyTip,
};

export default CoachmarkProvider;
